import React, { useState, useEffect, useMemo, useRef } from "react";
import { CSVLink } from "react-csv";
import API from "../../services/api";
import TableLoader from "../../shared/TableLoader";
import NoDataFound from "../../shared/NoDataFound";
import MultiSelectFilter from "../../shared/MultiSelectFilter";
import {
  formatSpentValue,
  getDayKey,
  formatDisplayDate,
  BrandsTypes,
} from "../../utils/constants";

const formatLocationLabel = (location = "") =>
  String(location)
    .replace(/[_-]+/g, " ")
    .replace(/\b\w/g, (char) => char.toUpperCase());

function LocationDateWiseReport({ dateRange, initiateDownloadCSV, setInitiateDownloadCSV, selectedSalesPersonId }) {
  const [locationData, setLocationData] = useState([]);
  const [loader, setLoader] = useState(false);
  const [csvData, setCsvData] = useState([]);
  const csvLinkRef = useRef(null);
  const [selectedColumnKeys, setSelectedColumnKeys] = useState([]);

  const fetchData = async () => {
    if (!dateRange?.[0] || !dateRange?.[1]) {
      setLocationData([]);
      return;
    }
    try {
      setLoader(true);
      const response = await API({
        url: "/alpha/get-location-date-month-wise-reports",
        method: "POST",
        responseType: "json",
        data: { dateRange, isMonthWise: false, sales_person_id: +selectedSalesPersonId || undefined },
      });
      if (response?.code === 200) {
        setLocationData(Array.isArray(response.data) ? response.data : []);
      } else {
        setLocationData([]);
      }
    } catch (error) {
      console.error("Error fetching location data", error);
      setLocationData([]);
    } finally {
      setLoader(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [dateRange, selectedSalesPersonId]);

  const locationColumns = useMemo(() => {
    const filtered = (locationData || [])
    let unique = [...new Set(filtered.map((r) => `${r.location} - ${BrandsTypes.find((b) => b.value === r.new_user_type)?.label || ""}`).filter(Boolean))];
    unique = unique.sort((a, b) => String(a).localeCompare(String(b)));
    let toBeLastColsKeyWords = ['classified', 'inbound']
    let lastCols = unique.filter((col) => toBeLastColsKeyWords.some((keyword) => col.toLowerCase().includes(keyword)));
    let firstCols = unique.filter((col) => !lastCols.includes(col));
    return [...firstCols, ...lastCols];
  }, [locationData]);

  const visibleColumns = useMemo(
    () => (selectedColumnKeys.length ? locationColumns.filter((loc) => selectedColumnKeys.includes(loc)) : locationColumns),
    [locationColumns, selectedColumnKeys]
  );

  const rows = useMemo(() => {
    if (!dateRange?.[0] || !dateRange?.[1]) return [];

    const filtered = (locationData || [])

    const recordsByDay = new Map();
    filtered.forEach((record) => {
      const key = getDayKey(new Date(record.date * 1000));
      if (!recordsByDay.has(key)) recordsByDay.set(key, []);
      recordsByDay.get(key).push(record);
    });

    const start = new Date(dateRange[0]);
    const end = new Date(dateRange[1]);
    start.setHours(0, 0, 0, 0);
    end.setHours(0, 0, 0, 0);

    const result = [];
    for (let current = new Date(start); current <= end; current.setDate(current.getDate() + 1)) {
      const currentDate = new Date(current);
      const dayKey = getDayKey(currentDate);
      const dayRecords = recordsByDay.get(dayKey) || [];

      const totalSpent = dayRecords.reduce(
        (sum, r) => sum + Number(r.totalSpent || 0),
        0
      );
      const byLocation = {};
      locationColumns.forEach((loc) => {
        const matched = dayRecords.filter((r) => `${r.location} - ${BrandsTypes.find((b) => b.value === r.new_user_type)?.label || ""}` === loc);
        byLocation[loc] = matched.reduce(
          (acc, r) => ({
            ads: acc.ads + Number(r.totalAds || 0),
            spent: acc.spent + Number(r.totalSpent || 0),
          }),
          { ads: 0, spent: 0 }
        );
      });

      result.push({
        date: currentDate,
        totalSpent,
        byLocation,
      });
    }
    return result;
  }, [dateRange, locationColumns, locationData]);

  const grandTotals = useMemo(() => {
    const totalSpent = rows.reduce((sum, r) => sum + r.totalSpent, 0);
    const byLocation = {};
    locationColumns.forEach((loc) => {
      byLocation[loc] = rows.reduce(
        (acc, r) => ({
          ads: acc.ads + (r.byLocation[loc]?.ads || 0),
          spent: acc.spent + (r.byLocation[loc]?.spent || 0),
        }),
        { ads: 0, spent: 0 }
      );
    });
    return { totalSpent, byLocation };
  }, [rows, locationColumns]);

  const colspan = 2 + visibleColumns.length * 2;

  const handleDownloadCSV = () => {
    if (!locationColumns.length || !rows.length) return;
    const csvRows = rows.map((row) => {
      const obj = {
        "Date of Campaign": formatDisplayDate(row.date),
        "SUM of Base Price (Excluding GST)": row.totalSpent ?? "",
      };
      locationColumns.forEach((loc) => {
        const label = formatLocationLabel(loc);
        obj[`${label} - Count`] = row.byLocation[loc]?.ads ?? "";
        obj[`${label} - Revenue (Excl GST)`] = row.byLocation[loc]?.spent ?? "";
      });
      return obj;
    });
    const totalRow = {
      "Date of Campaign": "Grand Total",
      "SUM of Base Price (Excluding GST)": grandTotals.totalSpent ?? "",
      ...Object.fromEntries(
        locationColumns.flatMap((loc) => {
          const label = formatLocationLabel(loc);
          const t = grandTotals.byLocation[loc] || { ads: 0, spent: 0 };
          return [
            [`${label} - Count`, t.ads ?? ""],
            [`${label} - Revenue (Excl GST)`, t.spent ?? ""],
          ];
        })
      ),
    };
    setCsvData([...csvRows, totalRow]);
  };

  useEffect(() => {
    if (csvData.length > 0) {
      csvLinkRef.current?.link?.click();
      setCsvData([]);
    }
  }, [csvData]);


  useEffect(() => {
    if (initiateDownloadCSV) {
      console.log("initiateDownloadCSV");
      handleDownloadCSV();
      setInitiateDownloadCSV(false);
    }
  }, [initiateDownloadCSV]);

  const csvFilename = `location_date_wise_report_${dateRange?.[0] ? new Date(dateRange[0]).toISOString().slice(0, 10) : "start"}_${dateRange?.[1] ? new Date(dateRange[1]).toISOString().slice(0, 10) : "end"}.csv`;

  return (
    <div className="alpha-report alpha-report-location-date-wise mt-3">
        <CSVLink data={csvData} filename={csvFilename} className="d-none" ref={csvLinkRef} />
      <MultiSelectFilter
        label="Columns"
        options={locationColumns.map((loc) => ({ value: loc, label: formatLocationLabel(loc) }))}
        selected={selectedColumnKeys}
        onChange={setSelectedColumnKeys}
      />
      <div className="table-responsive alpha-report-table-wrap">
        <table className="table table-bordered table-sm align-middle alpha-report-table">
          <colgroup>
            <col style={{ width: "var(--sticky-width)" }} />
            <col className="alpha-report-col-sum" />
            {visibleColumns.flatMap((loc) => [
              <col key={`${loc}-count`} style={{ width: 72 }} />,
              <col key={`${loc}-rev`} className="alpha-report-col-location" />,
            ])}
          </colgroup>
          <thead>
            <tr>
              <th rowSpan={2} className="alpha-report-th-sticky-first align-middle">
                Date
              </th>
              <th rowSpan={2} className="alpha-report-th-month alpha-report-th-location align-middle" style={{ minWidth: 160 }}>
                SUM of Base Price (Excluding GST)
              </th>
              {visibleColumns.map((loc) => (
                <th
                  key={loc}
                  colSpan={2}
                  className="text-center alpha-report-th-month alpha-report-th-location"
                  title={formatLocationLabel(loc)}
                  style={{ minWidth: 140 }}
                >
                  {formatLocationLabel(loc)}
                </th>
              ))}
            </tr>
            <tr>
              {visibleColumns.map((loc) => (
                <React.Fragment key={`${loc}-sub`}>
                  <th className="text-center alpha-report-th-sub small">Count</th>
                  <th className="text-center alpha-report-th-sub small">Revenue</th>
                </React.Fragment>
              ))}
            </tr>
          </thead>
          {loader ? (
            <TableLoader colspan={colspan} />
          ) : !visibleColumns.length ? (
            <NoDataFound colspan={colspan} />
          ) : (
            <tbody>
              {rows.map((row) => (
                <tr key={getDayKey(row.date)}>
                  <td
                    title={formatDisplayDate(row.date)}
                    className="alpha-report-td-sticky-first alpha-report-td-sticky-first-truncate"
                  >
                    {formatDisplayDate(row.date)}
                  </td>
                  <td title={row.totalSpent}>{row.totalSpent ? formatSpentValue(row.totalSpent) : ""}</td>
                  {visibleColumns.map((loc) => {
                    const cell = row.byLocation[loc] || { ads: 0, spent: 0 };
                    return (
                      <React.Fragment key={loc}>
                        <td className="text-center">{cell.ads || 0}</td>
                        <td className="text-center" title={cell.spent}>
                          {cell.spent ? formatSpentValue(cell.spent) : ""}
                        </td>
                      </React.Fragment>
                    );
                  })}
                </tr>
              ))}
              <tr className="fw-bold">
                <td className="alpha-report-td-grand-total-sticky">Grand Total</td>
                <td title={grandTotals.totalSpent} className="alpha-report-td-grand-total">
                  {grandTotals.totalSpent ? formatSpentValue(grandTotals.totalSpent) : ""}
                </td>
                {visibleColumns.map((loc) => {
                  const cell = grandTotals.byLocation[loc] || { ads: 0, spent: 0 };
                  return (
                    <React.Fragment key={loc}>
                      <td className="text-center alpha-report-td-grand-total">{cell.ads || 0}</td>
                      <td className="text-center alpha-report-td-grand-total" title={cell.spent}>
                        {cell.spent ? formatSpentValue(cell.spent) : ""}
                      </td>
                    </React.Fragment>
                  );
                })}
              </tr>
            </tbody>
          )}
        </table>
      </div>
    </div>
  );
}

export default LocationDateWiseReport;
